import '@/playlist/games/laneChooser/styles/lane-chooser-answers.scss';

import React, { useEffect, useImperativeHandle, useRef } from 'react';
import classNames from 'classnames';
import { gsap } from 'gsap';

import { SCREEN_SIZES } from '@/shared/Constants';
import { computeInitialLiftYFromWidth } from '@/playlist/games/laneChooser/Helpers';

import AnswerBox from '@/playlist/games/laneChooser/components/answers/AnswerBox';

export type AnswersPanelProps = {
	className?: string;
	topWidthPct?: number;
	lanesCount?: number;
	labels?: string[] | number[];
	confirmedIndex?: number | null;
	/** Boolean flags indicating whether each answer (by index) is correct. */
	answerCorrectFlags?: boolean[];
	// Animation control and environment
	laneLayerRef?: React.RefObject<HTMLDivElement | null>;
	characterRef?: React.RefObject<HTMLDivElement | null>;
	answersAtBottom?: boolean;
	resetKey?: any;
	pausedInitially?: boolean;
	gameStarted?: boolean;
	motionScale?: number;
	motionDirection?: 1 | -1;
	onShowNextQuestion?: () => void;
	onBottomTargetComputed?: (targetY: number) => void;
	onAnswersAtBottomChange?: (atBottom: boolean) => void;
	deltaSecondsForConfirmed?: number;
};

export type AnswersPanelHandle = {
	skipToNext: () => void;
};

const AnswersPanel = React.forwardRef<AnswersPanelHandle, AnswersPanelProps>((props, ref) => {
	const {
		className,
		topWidthPct,
		lanesCount,
		labels,
		confirmedIndex = null,
		answerCorrectFlags,
		laneLayerRef,
		characterRef,
		answersAtBottom,
		resetKey,
		pausedInitially,
		gameStarted,
		motionScale = 1,
		motionDirection = 1,
		onShowNextQuestion,
		onBottomTargetComputed,
		onAnswersAtBottomChange,
		deltaSecondsForConfirmed,
	} = props;
	const safeLabels = Array.isArray(labels) ? labels : [];
	const count = Math.min(safeLabels.length || 4, 4);
	const getLabel = (i: number) => (safeLabels[i] != null ? String(safeLabels[i]) : '');

	// Merge forwarded ref with local
	const localRef = useRef<HTMLDivElement | null>(null);
	const setRef = (node: HTMLDivElement | null) => {
		localRef.current = node;
	};

	// Internal state for motion and timeline
	const tlRef = useRef<gsap.core.Timeline | null>(null);
	const bottomTargetYRef = useRef(0);

	// Build timeline when dependencies change
	useEffect(() => {
		const answers = localRef.current;
		const host = laneLayerRef?.current || null;
		const character = characterRef?.current || null;
		if (!answers || !host || !character) return;

		const ctx = gsap.context(() => {
			const hostRect = host.getBoundingClientRect();
			const answersRect = answers.getBoundingClientRect();
			const characterRect = character.getBoundingClientRect();
			const clearanceFactor = window.innerWidth < SCREEN_SIZES.DESKTOP ? 0.8 : 1;
			const clearance = characterRect.height * clearanceFactor;
			const targetY = Math.max(0, hostRect.height - answersRect.height - clearance);
			bottomTargetYRef.current = targetY;
			onBottomTargetComputed?.(targetY);

			gsap.set(answers, { zIndex: 1, scale: 0.8 });

			const tl = gsap.timeline({ paused: Boolean(pausedInitially ?? true) });
			tlRef.current = tl;

			const initialY = computeInitialLiftYFromWidth();
			tl.to(answers, { y: initialY, scale: 1, duration: 2, ease: 'linear' });
			tl.set(answers, { zIndex: 3 });
			tl.to(answers, {
				y: targetY,
				scale: 2,
				duration: 34,
				ease: 'linear',
				onComplete: () => {
					gameStarted && onAnswersAtBottomChange?.(true);
				},
			});
		}, host);
		return () => {
			tlRef.current = null;
			ctx.revert();
		};
	}, [
		laneLayerRef,
		characterRef,
		resetKey,
		pausedInitially,
		onAnswersAtBottomChange,
		onBottomTargetComputed,
		gameStarted,
	]);

	// Play on game start
	useEffect(() => {
		if (gameStarted && tlRef.current) {
			tlRef.current.play();
		}
	}, [gameStarted]);

	useEffect(() => {
		const tl = tlRef.current;
		if (!tl) return;
		const magnitude = Math.max(0.0001, Math.abs(motionScale));
		const signedScale = motionDirection === -1 ? -magnitude : magnitude;
		tl.timeScale(signedScale);
		const shouldPause = !gameStarted || !!answersAtBottom;
		tl.paused(shouldPause);
	}, [motionScale, motionDirection, answersAtBottom, gameStarted]);

	useImperativeHandle(
		ref,
		() => ({
			skipToNext: () => {
				const answers = localRef.current;
				const host = laneLayerRef?.current || null;
				if (!answers || !host) return;
				if (answersAtBottom) {
					const targetY = host.clientHeight + answers.clientHeight;
					gsap.timeline().to(answers, {
						y: targetY,
						scale: 3,
						duration: 5,
						ease: 'linear',
						onComplete: () => {
							onShowNextQuestion?.();
						},
					});
				}
			},
		}),
		[answersAtBottom, laneLayerRef, onShowNextQuestion],
	);

	const TWO_LANES_WIDTH_OFFSET_PCT = 15; // percent to subtract for 2-lane layout
	const DEFAULT_WIDTH_OFFSET_PCT = 5; // percent to subtract for other lane counts

	const SHIFT_TOWARD_LEFT_LINE = -0.01; // nudges the first box slightly toward the left lane line
	const SHIFT_TOWARD_RIGHT_LINE = 0.01; // nudges the last box slightly toward the right lane line
	const SECOND_BOX_LEFT_NUDGE = -0.01; // subtle left nudge for the 2nd box to avoid perfect symmetry
	const THIRD_BOX_RIGHT_NUDGE = 0.01; // subtle right nudge for the 3rd box to avoid perfect symmetry

	return (
		<div
			className={classNames('lane-chooser-answers', className)}
			style={{
				['--lane-top-width' as any]: `${
					Number(topWidthPct) -
					(lanesCount === 2 ? TWO_LANES_WIDTH_OFFSET_PCT : DEFAULT_WIDTH_OFFSET_PCT)
				}%`,
				visibility: gameStarted ? 'visible' : 'hidden',
			}}
			aria-hidden
			ref={setRef}
		>
			<div className="lane-chooser-answers__row">
				{new Array(count).fill(0).map((_, i) => {
					const isCorrect = Boolean(answerCorrectFlags?.[i]);
					const position: 'left' | 'right' | 'middle' =
						i === 0 ? 'left' : i === count - 1 ? 'right' : 'middle';
					// Compute horizontal position per box: first near its left lane line, last near right lane line, middle centered
					const lanes = Math.max(1, Math.min(4, Number(lanesCount) || count));
					const laneCenter = (i + 0.5) / lanes; // fraction across row (0..1)
					const withinLaneShift = (() => {
						if (i === 0 && lanes > 1) return SHIFT_TOWARD_LEFT_LINE / lanes; // slight bias toward left line
						if (i === lanes - 1 && lanes > 1) return SHIFT_TOWARD_RIGHT_LINE / lanes; // slight bias toward right line
						if (i === 1) return SECOND_BOX_LEFT_NUDGE;
						if (i === 2) return THIRD_BOX_RIGHT_NUDGE;
						return 0; // middle boxes centered exactly
					})();
					// Keep a tiny clamp to avoid extreme edges without affecting lane centers
					const frac = Math.min(0.98, Math.max(0.01, laneCenter + withinLaneShift));
					const leftPct = frac * 100;
					return (
						<div
							key={i}
							className="lane-chooser-answers__slot"
							style={{ left: `${leftPct}%` }}
						>
							<AnswerBox
								label={getLabel(i)}
								isActive={confirmedIndex === i}
								isCorrect={isCorrect}
								position={position}
								showCorrect={
									confirmedIndex != null &&
									!Boolean(answerCorrectFlags?.[confirmedIndex]) &&
									isCorrect
								}
								paused={answersAtBottom}
								deltaSeconds={
									confirmedIndex === i ? deltaSecondsForConfirmed : undefined
								}
							/>
						</div>
					);
				})}
			</div>
		</div>
	);
});

export default AnswersPanel;
